/*
 * Buy button guard.
 *
 * Three jobs:
 *  1. LOCK — the moment a submit is accepted, swallow every further submit on
 *     that form for `data-lock-ms`. This is what stops double-taps stacking
 *     items: the previous version cleared the cart per click but never blocked
 *     the second click, so two adds could both fire before navigation.
 *  2. PREFLIGHT — when replacing the cart, start /cart/clear.js on pointerdown
 *     instead of on submit, so the request overlaps the user's own click motion
 *     and is usually finished by the time the form submits.
 *  3. FEEDBACK — dim the button and reveal the theme's spinner while busy.
 */
(function () {
  if (window.__vrnBuyGuard) return;
  window.__vrnBuyGuard = true;

  var locks = new WeakMap();
  var preflight = new WeakMap();

  function lockMs(form) {
    var n = parseInt(form.dataset.lockMs || '3000', 10);
    return isNaN(n) ? 3000 : n;
  }

  function isLocked(form) {
    return Date.now() < (locks.get(form) || 0);
  }

  function setBusy(form, state) {
    var btn = form.querySelector('[type="submit"]');
    // product-form.js still owns the button (an add in flight, or the redirect
    // to checkout under way) — leave its busy state alone.
    if (!state && btn && btn.classList.contains('loading')) return;
    if (btn) btn.classList.toggle('atc-locked', state);
    var spinner = form.querySelector('.loading__spinner');
    if (spinner) spinner.classList.toggle('hidden', !state);
  }

  /* Re-submit after the guard's own work, through the normal handlers. Native
     form.submit() would skip product-form.js — and with it the AJAX add, the
     redirect to checkout and pending upsells — so it is only the last resort. */
  function resubmit(form) {
    var btn = form.querySelector('[type="submit"]');
    if (typeof form.requestSubmit === 'function') {
      form.requestSubmit(btn || undefined);
    } else {
      form.submit();
    }
  }

  /* Back from checkout via the back/forward cache: the page is restored exactly
     as it was left, lock and dimmed button included. Without this the buy button
     stayed dead after "back" in skip-to-checkout mode. */
  window.addEventListener('pageshow', function (event) {
    if (!event.persisted) return;
    var forms = document.querySelectorAll('form[data-atc-guard="true"]');
    for (var i = 0; i < forms.length; i++) {
      locks.set(forms[i], 0);
      preflight.delete(forms[i]);
      delete forms[i].dataset.guardPass;
      var btn = forms[i].querySelector('[type="submit"]');
      if (btn) btn.classList.remove('atc-locked');
      if (btn && !btn.classList.contains('loading')) {
        var spinner = forms[i].querySelector('.loading__spinner');
        if (spinner) spinner.classList.add('hidden');
      }
    }
  });

  function clearCart() {
    return fetch('/cart/clear.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    }).catch(function (e) {
      console.error('[buy-guard] cart clear failed, adding anyway', e);
    });
  }

  /* 2. Preflight the clear on pointerdown. */
  document.addEventListener(
    'pointerdown',
    function (event) {
      var target = event.target;
      if (!target || typeof target.closest !== 'function') return;
      var btn = target.closest('form[data-atc-guard="true"] [type="submit"]');
      if (!btn) return;
      var form = btn.form || btn.closest('form');
      if (!form || form.dataset.replaceCart !== 'true') return;
      // Only preflight when the page is about to navigate away. In drawer mode
      // the shopper stays here, and emptying the cart on pointerdown makes any
      // app-rendered bundle widget re-render mid-press — even if the press is
      // then abandoned. There, the clear waits until the submit is committed.
      if (form.dataset.skipCart !== 'true') return;
      if (isLocked(form)) return;
      preflight.set(form, clearCart());
    },
    true
  );

  /* 1 + 3. Capture phase on document, so this runs before <product-form>. */
  document.addEventListener(
    'submit',
    function (event) {
      var form = event.target;
      if (!form || typeof form.matches !== 'function') return;
      if (!form.matches('form[data-atc-guard="true"]')) return;

      // Our own re-submit after clearing — let the normal handlers take it.
      if (form.dataset.guardPass === '1') {
        delete form.dataset.guardPass;
        return;
      }

      if (isLocked(form)) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }

      locks.set(form, Date.now() + lockMs(form));
      setBusy(form, true);

      // Always release after the cooldown. Skip-to-checkout used to keep the
      // lock forever on the assumption the page had left — but if the add
      // failed, or an app cancelled the submit, the button stayed dead.
      // product-form.js keeps the button busy itself while it redirects.
      window.setTimeout(function () {
        locks.set(form, 0);
        setBusy(form, false);
      }, lockMs(form));

      if (form.dataset.replaceCart !== 'true') return;

      event.preventDefault();
      event.stopPropagation();

      var pending = preflight.get(form) || clearCart();
      preflight.delete(form);

      pending.then(function () {
        form.dataset.guardPass = '1';
        resubmit(form);
      });
    },
    true
  );
})();
